import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import BookingExperience from "../components/BookingExperience";
import specialistImage from "../assets/spec1.png";
import { getSavedUserSession, getImageUrl, publicApi, publicClientApi } from "../lib/api";

function formatDuration(time) {
  if (!time) return "";
  const timeStr = String(time).trim();
  if (/[a-zA-Z]/.test(timeStr)) {
    return timeStr;
  }
  if (timeStr.includes(":")) {
    const parts = timeStr.split(":");
    const hrs = parseInt(parts[0], 10);
    const mins = parseInt(parts[1], 10);
    if (!isNaN(hrs) && !isNaN(mins)) {
      if (hrs > 0 && mins > 0) {
        return `${hrs} Hr${hrs > 1 ? "s" : ""} ${mins} Min`;
      } else if (hrs > 0) {
        return `${hrs} Hr${hrs > 1 ? "s" : ""}`;
      } else if (mins > 0) {
        return `${mins} Min`;
      }
    }
  }
  if (timeStr.includes(".")) {
    const val = parseFloat(timeStr);
    if (!isNaN(val)) {
      if (val < 10) {
        const hrs = Math.floor(val);
        const mins = Math.round((val - hrs) * 60);
        if (hrs > 0 && mins > 0) {
          return `${hrs} Hr${hrs > 1 ? "s" : ""} ${mins} Min`;
        } else if (hrs > 0) {
          return `${hrs} Hr${hrs > 1 ? "s" : ""}`;
        } else if (mins > 0) {
          return `${mins} Min`;
        }
      } else {
        return `${Math.round(val)} Min`;
      }
    }
  }
  const mins = parseInt(timeStr, 10);
  if (isNaN(mins)) return timeStr;
  if (mins < 60) {
    return `${mins} Min`;
  }
  const hrs = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  if (remainingMins === 0) {
    return `${hrs} Hr${hrs > 1 ? "s" : ""}`;
  }
  return `${hrs} Hr${hrs > 1 ? "s" : ""} ${remainingMins} Min`;
}

export default function BookingPage() {
  const { vendorSlug } = useParams();
  const [vendorProfile, setVendorProfile] = useState(null);
  const [vendorServices, setVendorServices] = useState([]);
  const [isLoadingVendor, setIsLoadingVendor] = useState(true);
  const [vendorError, setVendorError] = useState("");
  const [userSession, setUserSession] = useState(() => getSavedUserSession());
  const [recentBookings, setRecentBookings] = useState([]);

  // Load vendor profile + services by slug (from the URL the vendor shared)
  useEffect(() => {
    if (!vendorSlug) return;
    setIsLoadingVendor(true);
    setVendorError("");
    Promise.all([
      publicApi.getProfileBySlug(vendorSlug),
      publicApi.getServicesBySlug(vendorSlug),
    ])
      .then(([profileRes, servicesRes]) => {
        setVendorProfile(profileRes.data);
        setVendorServices(servicesRes.data || []);
        setIsLoadingVendor(false);
      })
      .catch((error) => {
        console.error("Error loading vendor:", error);
        setVendorError(error.message || "Unable to load vendor profile.");
        setIsLoadingVendor(false);
      });
  }, [vendorSlug]);

  // After a booking is confirmed, refresh the user's bookings list
  const handleBookingConfirmed = () => {
    const customerId = userSession?.user?.id || userSession?.userId || userSession?.vendorClientId;
    if (!customerId) return;
    publicClientApi.getClientBookings(customerId).then((res) => {
      setRecentBookings(res.data || res.bookings || []);
    }).catch(() => { });
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoadingVendor) {
    return (
      <div className="min-h-screen bg-[#F5F9FF] font-sans flex items-center justify-center">
        <div className="text-center py-12">
          <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent" role="status" />
          <p className="mt-4 text-sm font-bold text-blue-700">Loading booking profile…</p>
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (vendorError) {
    return (
      <div className="min-h-screen bg-[#F5F9FF] font-sans flex items-center justify-center">
        <div className="text-center rounded-3xl border border-red-100 bg-white p-10 shadow-xl max-w-md mx-auto">
          <i className="fa-solid fa-triangle-exclamation text-4xl text-red-400 mb-4" />
          <h2 className="text-xl font-extrabold text-slate-900">Booking link not found</h2>
          <p className="mt-2 text-sm font-semibold text-slate-500">
            {vendorError}
          </p>
          <p className="mt-4 text-xs text-slate-400">
            Please check the link you received from your artist or contact them directly.
          </p>
        </div>
      </div>
    );
  }

  // ── Derived display values ─────────────────────────────────────────────────
  const displayName = vendorProfile?.fullname || vendorProfile?.name || "Your Artist";
  const displayCategory = vendorProfile?.category || "Beauty & Wellness";
  const displayCity = vendorProfile?.city || "";
  const displayState = vendorProfile?.state || "";
  const displayLocation = [displayCity, displayState].filter(Boolean).join(", ");
  const profileImage = vendorProfile?.profile_image
    ? getImageUrl(vendorProfile.profile_image)
    : specialistImage;

  return (
    <div className="min-h-screen bg-[#F5F9FF] font-sans text-slate-800">
      <Navbar />

      <main className="mx-auto max-w-[1536px] px-4 py-8 sm:px-6 lg:px-10">

        {/* ── Vendor Hero Banner ─────────────────────────────────────────── */}
        <section className="relative mb-8 overflow-hidden rounded-[32px] border border-blue-100 bg-white shadow-[0_24px_70px_rgba(14,63,182,0.10)]">
          {/* Decorative background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-[#EAF2FF] pointer-events-none" />
          <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-blue-200/25 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-10 -left-10 h-48 w-48 rounded-full bg-purple-100/30 blur-2xl pointer-events-none" />

          <div className="relative grid gap-6 p-6 md:grid-cols-[1fr_auto] md:items-center lg:p-8">
            {/* Left: profile info */}
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
              <div className="relative">
                <img
                  src={profileImage}
                  alt={displayName}
                  className="h-24 w-24 rounded-3xl object-cover shadow-xl shadow-blue-100 sm:h-28 sm:w-28 ring-4 ring-white"
                />
                <span className="absolute -bottom-2 -right-2 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg ring-2 ring-white">
                  <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                </span>
              </div>

              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-blue-600 px-3 py-1 text-xs font-bold uppercase tracking-[0.16em] text-white">
                    Online Booking
                  </span>
                  <span className="rounded-full bg-emerald-50 border border-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
                    Accepting appointments
                  </span>
                </div>

                <h1 className="mt-3 text-3xl font-extrabold text-[#191E49] sm:text-4xl">
                  {displayName}
                </h1>

                <p className="mt-2 max-w-2xl text-sm font-semibold leading-6 text-slate-600">
                  {vendorProfile?.bio || `${displayCategory}. Choose a service, pick a date, and confirm your appointment — all online.`}
                </p>

                <div className="mt-4 flex flex-wrap gap-3 text-sm font-bold text-slate-600">
                  <span className="flex items-center gap-1.5 rounded-xl bg-white/80 px-3 py-2 shadow-sm border border-slate-100">
                    <i className="fa-solid fa-star text-[#F8D247] text-xs" aria-hidden="true" />
                    Top rated
                  </span>
                  {displayLocation && (
                    <span className="flex items-center gap-1.5 rounded-xl bg-white/80 px-3 py-2 shadow-sm border border-slate-100">
                      <i className="fa-solid fa-location-dot text-blue-600 text-xs" aria-hidden="true" />
                      {displayLocation}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 rounded-xl bg-white/80 px-3 py-2 shadow-sm border border-slate-100">
                    <i className="fa-regular fa-clock text-blue-600 text-xs" aria-hidden="true" />
                    Instant confirmation
                  </span>
                </div>
              </div>
            </div>

            {/* Right: quick stats */}
            <div className="grid gap-3 rounded-3xl border border-blue-100 bg-white/85 p-4 shadow-sm sm:grid-cols-3 md:min-w-[300px] md:grid-cols-1">
              {[
                {
                  icon: "fa-solid fa-scissors",
                  label: "Services",
                  value: String(vendorServices?.length || 0),
                },
                {
                  icon: "fa-regular fa-calendar-check",
                  label: "Availability",
                  value: "Live",
                },
                {
                  icon: "fa-solid fa-shield-halved",
                  label: "Secure booking",
                  value: "OTP verified",
                },
              ].map(({ icon, label, value }) => (
                <div key={label} className="rounded-2xl bg-blue-50/70 p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <i className={`${icon} text-blue-600 text-xs`} aria-hidden="true" />
                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-blue-700">{label}</p>
                  </div>
                  <p className="text-lg font-extrabold text-[#191E49]">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── How it works strip ─────────────────────────────────────────── */}
        <section className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {[
            { step: "1", icon: "fa-solid fa-spa", title: "Pick a service", desc: "Browse all available treatments and services" },
            { step: "2", icon: "fa-regular fa-calendar", title: "Choose a date & time", desc: "Select from real-time available slots" },
            { step: "3", icon: "fa-solid fa-circle-check", title: "Confirm instantly", desc: "Verify with your phone and you're booked" },
          ].map(({ step, icon, title, desc }) => (
            <div key={step} className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-blue-600 text-white font-extrabold text-sm shadow-lg shadow-blue-200">
                {step}
              </span>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <i className={`${icon} text-blue-500 text-xs`} aria-hidden="true" />
                  <p className="text-sm font-extrabold text-slate-900">{title}</p>
                </div>
                <p className="text-xs font-semibold text-slate-500 leading-5">{desc}</p>
              </div>
            </div>
          ))}
        </section>

        {/* ── Booking Experience widget ──────────────────────────────────── */}
        <BookingExperience
          vendorProfile={vendorProfile}
          vendorServices={vendorServices}
          userSession={userSession}
          onSessionChange={setUserSession}
          onBookingConfirmed={handleBookingConfirmed}
        />

        {/* ── Recent bookings (shown after user logs in and books) ───────── */}
        {/* {recentBookings.length > 0 && (
          <section className="mt-8 rounded-2xl border border-emerald-100 bg-white p-6 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">Your appointments</p>
            <h2 className="mt-1 text-xl font-extrabold text-[#191E49]">Recent bookings</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {recentBookings.slice(0, 6).map((booking, index) => (
                <div
                  key={booking.id || booking.booking_id || index}
                  className="rounded-xl border border-slate-100 bg-slate-50 p-4"
                >
                  <p className="font-extrabold text-slate-900 text-sm">
                    {booking.service?.name || booking.service_name || "Appointment"}
                    {(booking.service?.time || booking.duration) ? ` (${formatDuration(booking.service?.time || booking.duration)})` : ""}
                  </p>
                  <p className="mt-1 text-xs font-semibold text-slate-500">
                    {booking.booking_date || booking.date || "Date TBD"}
                    {(booking.booking_time || booking.time) ? ` · ${(booking.booking_time || booking.time).split("-")[0].trim()}` : ""}
                  </p>
                  <span
                    className={`mt-2 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                      booking.status === "confirmed"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-blue-50 text-blue-700"
                    }`}
                  >
                    {booking.status || "Pending"}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )} */}

      </main>

      <Footer />
    </div>
  );
}
